(() => {
  'use strict';
  if (window.__SQ_FAVORITE_CARD_CONTROLS__) return;
  window.__SQ_FAVORITE_CARD_CONTROLS__ = true;
  window.__SQ_FAVORITE_CARD_CONTROLS_V2__ = true;

  /* Search behavior is the single favorite interaction owner.
     Native Search cards keep .sq-card-favorite-control.
     Collection/Home keep their existing .sq-universal-card__favorite visual,
     but this runtime owns the save/remove behavior for both. */
  const CONTROL = '[data-sq-card-favorite-control],[data-sq-universal-favorite]';
  const PRODUCT_LINK = 'a[href*="/products/"]';
  const HOST = 'product-card,[data-sq-universal-card],.sq-product-card,.premium-recommend-card-shell,.premium-gallery-card,.sq-search-card,.sq-search-grid-card';
  const productCache = new Map();

  const rootPath = () => String(window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  const text = value => String(value || '').trim();
  const storageKey = () => `lcs-favorites-${document.documentElement.dataset.customerId || window.LCS_CUSTOMER_ID || 'guest'}`;

  const read = () => {
    try {
      if (window.LCSFavorites?.read) return window.LCSFavorites.read();
      const parsed = JSON.parse(localStorage.getItem(storageKey()) || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) { return []; }
  };

  const write = items => {
    if (window.LCSFavorites?.write) return window.LCSFavorites.write(items);
    try { localStorage.setItem(storageKey(), JSON.stringify(items)); } catch (_) {}
    const detail = { items, count: items.length };
    window.dispatchEvent(new CustomEvent('lcs:favorites:change', { detail }));
    window.dispatchEvent(new CustomEvent('lcs:favorites-updated', { detail }));
    return items;
  };

  const parseUrl = value => {
    try { return new URL(value || location.href, location.origin); }
    catch (_) { return new URL(location.href); }
  };
  const handleFromUrl = value => {
    const match = parseUrl(value).pathname.match(/\/products\/([^/?#]+)/i);
    return match ? decodeURIComponent(match[1]) : '';
  };
  const variantFromUrl = value => parseUrl(value).searchParams.get('variant') || '';
  const hostFor = button => button?.closest?.(HOST) || null;

  function cardData(host) {
    const link = host?.querySelector?.(PRODUCT_LINK);
    const url = link?.href || host?.dataset?.productUrl || location.href;
    const parsed = parseUrl(url);
    const image = host?.querySelector?.('img:not(.product-card-variant-belt__image)');
    const titleNode = host?.querySelector?.('.sq-universal-card__title,.sq-product-card__title,.sq-search-card__title,[ref="productTitleLink"],h3,h2,small');
    const priceNode = host?.querySelector?.('[data-sq-current-price],.sq-product-card__price-current,.sq-search-card__price,[class*="price"]');
    return {
      productId: text(host?.dataset?.productId || host?.dataset?.sqProductId),
      productHandle: text(host?.dataset?.productHandle || handleFromUrl(url)),
      productTitle: text(host?.dataset?.productTitle || titleNode?.textContent),
      productUrl: `${parsed.pathname}${parsed.search}`,
      productImage: text(host?.dataset?.productImage || image?.currentSrc || image?.src),
      productPrice: text(host?.dataset?.productPrice || priceNode?.textContent),
      variantId: text(host?.dataset?.selectedVariantId || host?.dataset?.variantId || variantFromUrl(url))
    };
  }

  function refreshDynamicData(button) {
    if (!(button instanceof HTMLElement)) return;
    const host = hostFor(button);
    if (host) {
      const data = cardData(host);
      Object.entries(data).forEach(([key, value]) => {
        if (text(value)) button.dataset[key] = text(value);
      });
    }

    const premium = button.closest('.premium-product-page');
    if (premium && button.dataset.sqPremiumMain === 'true') {
      const variantInput = premium.querySelector('[data-premium-variant-id]');
      const image = premium.querySelector('.premium-gallery-media.is-active img,.premium-gallery-card__stage img');
      button.dataset.productId = text(premium.dataset.productId || button.dataset.productId);
      button.dataset.productHandle = handleFromUrl(location.href);
      button.dataset.productTitle = text(premium.querySelector('.premium-title')?.textContent);
      button.dataset.productUrl = location.pathname + location.search;
      button.dataset.productImage = text(image?.currentSrc || image?.src);
      button.dataset.productPrice = text(premium.querySelector('[data-premium-price]')?.textContent);
      if (variantInput?.value) button.dataset.variantId = text(variantInput.value);
    }
  }

  const sameProduct = (item, button) => {
    const productId = text(button.dataset.productId);
    const handle = text(button.dataset.productHandle);
    return Boolean((productId && text(item.productId) === productId) || (handle && text(item.handle) === handle));
  };

  function syncButton(button) {
    if (!(button instanceof HTMLElement)) return;
    refreshDynamicData(button);
    const saved = read().some(item => sameProduct(item, button));
    const title = text(button.dataset.productTitle) || 'product';
    button.classList.toggle('is-saved', saved);
    button.toggleAttribute('data-sq-favorite-saved', saved);
    button.setAttribute('aria-pressed', saved ? 'true' : 'false');
    button.setAttribute('aria-label', `${saved ? 'Remove' : 'Save'} ${title} ${saved ? 'from' : 'to'} favorites`);

    if (saved) {
      button.style.setProperty('display', 'grid', 'important');
      button.style.setProperty('opacity', '1', 'important');
      button.style.setProperty('visibility', 'visible', 'important');
      button.style.setProperty('pointer-events', 'auto', 'important');
      button.style.setProperty('transform', 'none', 'important');
    } else {
      ['display','opacity','visibility','pointer-events','transform'].forEach(name => button.style.removeProperty(name));
    }
  }

  function syncAll(scope = document) {
    if (scope instanceof Element && scope.matches(CONTROL)) syncButton(scope);
    scope.querySelectorAll?.(CONTROL).forEach(syncButton);
  }

  const heartMarkup = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 21s-7.15-4.35-9.55-8.65C.72 9.24 2.55 5.55 6.1 5.15A5.05 5.05 0 0 1 12 8.05a5.05 5.05 0 0 1 5.9-2.9c3.55.4 5.38 4.09 3.65 7.2C19.15 16.65 12 21 12 21Z"></path></svg>';

  function createButton(data = {}) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'sq-card-favorite-control';
    button.setAttribute('data-sq-card-favorite-control', '');
    button.setAttribute('aria-pressed', 'false');
    button.innerHTML = heartMarkup;
    Object.entries({
      productId: data.productId,
      productHandle: data.productHandle,
      productTitle: data.productTitle,
      productUrl: data.productUrl,
      productImage: data.productImage,
      productPrice: data.productPrice,
      variantId: data.variantId
    }).forEach(([key, value]) => { if (text(value)) button.dataset[key] = text(value); });
    return button;
  }

  async function getProduct(handle) {
    if (!handle) return null;
    if (productCache.has(handle)) return productCache.get(handle);
    const request = fetch(`${rootPath()}products/${encodeURIComponent(handle)}.js`, {
      credentials: 'same-origin', cache: 'force-cache', headers: { Accept: 'application/json' }
    }).then(response => response.ok ? response.json() : null).catch(() => null);
    productCache.set(handle, request);
    return request;
  }

  async function hydrate(button) {
    refreshDynamicData(button);
    if (button.dataset.productId && button.dataset.variantId) return;
    const handle = button.dataset.productHandle || handleFromUrl(button.dataset.productUrl);
    const product = await getProduct(handle);
    if (!product) return;
    const variant = product.variants?.find(item => item.available) || product.variants?.[0];
    button.dataset.productId = text(product.id || button.dataset.productId);
    button.dataset.productHandle = text(product.handle || handle);
    button.dataset.productTitle = text(product.title || button.dataset.productTitle);
    button.dataset.productUrl = button.dataset.productUrl || `/products/${product.handle}`;
    button.dataset.productImage = button.dataset.productImage || product.featured_image || product.images?.[0] || '';
    button.dataset.productPrice = button.dataset.productPrice || text(product.price);
    if (variant?.id) button.dataset.variantId = text(variant.id);
  }

  function buildItem(button) {
    refreshDynamicData(button);
    const productId = text(button.dataset.productId);
    const variantId = text(button.dataset.variantId);
    const handle = text(button.dataset.productHandle);
    const title = text(button.dataset.productTitle) || 'Favorite item';
    const url = text(button.dataset.productUrl) || (handle ? `/products/${handle}` : '#');
    const image = text(button.dataset.productImage);
    const price = text(button.dataset.productPrice);
    return {
      key: `${productId || handle || 'product'}:${variantId || 'variant'}`,
      productId, variantId, handle, title, url, image,
      price, priceText: price, compareAtPrice: '',
      options: [], selectedOptions: [], optionsWithValues: [], variants: [], media: [],
      quantity: 1, available: true
    };
  }

  async function toggle(button) {
    refreshDynamicData(button);
    const current = read();
    const saved = current.some(item => sameProduct(item, button));
    if (saved) {
      write(current.filter(item => !sameProduct(item, button)));
      syncAll();
      return;
    }
    button.disabled = true;
    try {
      await hydrate(button);
      const item = buildItem(button);
      write([item, ...read().filter(favorite => !sameProduct(favorite, button))]);
      syncAll();
    } finally { button.disabled = false; }
  }

  function enhanceNativeProductCards(scope = document) {
    const cards = [];
    if (scope instanceof Element && scope.matches('product-card')) cards.push(scope);
    scope.querySelectorAll?.('product-card').forEach(card => cards.push(card));
    cards.forEach(card => {
      if (card.dataset.placeholder === 'true' || card.querySelector(CONTROL)) return;
      const data = cardData(card);
      if (!data.productHandle && !data.productId) return;
      card.append(createButton(data));
    });
  }

  function enhanceSurfaceCards(scope = document) {
    const main = document.getElementById('MainContent');
    const template = text(main?.dataset.template).toLowerCase();
    if (!template.includes('collection') && !template.includes('product')) return;
    const cards = [];
    if (scope instanceof Element && scope.matches('.sq-product-card')) cards.push(scope);
    scope.querySelectorAll?.('.sq-product-card').forEach(card => cards.push(card));
    cards.forEach(card => {
      if (card.closest('[data-sq-universal-card]') || card.querySelector(CONTROL)) return;
      const data = cardData(card);
      if (!data.productHandle && !data.productId) return;
      card.classList.add('has-sq-card-favorite');
      card.append(createButton(data));
    });
  }

  function enhancePremiumProductPage(scope = document) {
    const main = document.getElementById('MainContent');
    if (!text(main?.dataset.template).toLowerCase().includes('product')) return;
    const pages = [];
    if (scope instanceof Element && scope.matches('.premium-product-page')) pages.push(scope);
    scope.querySelectorAll?.('.premium-product-page').forEach(page => pages.push(page));
    pages.forEach(page => {
      const gallery = page.querySelector('.premium-gallery-card');
      if (gallery && !gallery.querySelector(CONTROL)) {
        gallery.classList.add('has-sq-card-favorite');
        const button = createButton({
          productId: page.dataset.productId,
          productHandle: handleFromUrl(location.href),
          productTitle: text(page.querySelector('.premium-title')?.textContent),
          productUrl: location.pathname + location.search,
          productImage: page.querySelector('.premium-gallery-media.is-active img,.premium-gallery-card__stage img')?.currentSrc || '',
          productPrice: text(page.querySelector('[data-premium-price]')?.textContent),
          variantId: page.querySelector('[data-premium-variant-id]')?.value || ''
        });
        button.dataset.sqPremiumMain = 'true';
        gallery.append(button);
      }
    });
  }

  function enhance(scope = document) {
    enhanceNativeProductCards(scope);
    enhanceSurfaceCards(scope);
    enhancePremiumProductPage(scope);
    syncAll(scope);
  }

  document.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : null;
    const button = target?.closest(CONTROL);
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    void toggle(button);
  }, true);

  /* Touch reveal intentionally lives only in sq-mobile-interaction-authority-v1.js. */
  ['lcs:favorites:change','lcs:favorites-updated','storage'].forEach(name => {
    window.addEventListener(name, () => syncAll());
  });
  document.addEventListener('sq:favorite-controls:sync', event => {
    const scope = event.detail?.scope;
    syncAll(scope instanceof Element ? scope : document);
  });
  document.addEventListener('shopify:section:load', event => enhance(event.target));

  const start = () => {
    enhance(document);
    setTimeout(() => syncAll(), 120);
    new MutationObserver(records => {
      records.forEach(record => record.addedNodes.forEach(node => {
        if (node instanceof Element) enhance(node);
      }));
    }).observe(document.body, { childList: true, subtree: true });
  };

  window.SQFavoriteCardControls = Object.assign(window.SQFavoriteCardControls || {}, {
    enhance, syncButton, syncAll, toggle
  });

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', start, { once: true })
    : start();
})();
